import { useCallback } from 'react'
import { CampaignStatus } from '../../../gql/types'
import {
  useFiltersPersistence,
  FiltersState,
} from '../../../hooks/useFiltersPersistence'

/**
 * CAMPAIGNS-SPECIFIC FILTER HOOK
 *
 * This hook wraps the generic useFiltersPersistence hook with campaign-specific
 * filter types and convenience methods. It provides:
 *
 * 1. Type-safe interface for campaign filter fields (query, status)
 * 2. Automatic localStorage persistence (key: 'brand-campaigns-filters-{brandId}')
 * 3. Convenience method for updating status filters
 * 4. Cross-tab synchronization
 *
 * Usage in Campaigns component:
 * const { filters, updateQuery, updateStatus } = useCampaignFilters(brand.id)
 *
 * Flow:
 * User types in search -> updateQuery() -> localStorage -> cross-tab sync
 * User clicks filter -> updateStatus() -> localStorage -> API refetch
 */

// Campaigns-specific filter interface extending the generic one
export interface CampaignFiltersState extends FiltersState {
  status: {
    [CampaignStatus.draft]: boolean
    [CampaignStatus.active]: boolean
    [CampaignStatus.paused]: boolean
    [CampaignStatus.completed]: boolean
    // [CampaignStatus.refunded]: boolean
  }
}

// Default filters for campaigns - this is what gets loaded on first visit
const DEFAULT_CAMPAIGN_FILTERS: CampaignFiltersState = {
  query: '', // Query is NOT persistent (will be ignored)
  status: {
    // All status filters off initially
    [CampaignStatus.draft]: false,
    [CampaignStatus.active]: false,
    [CampaignStatus.paused]: false,
    [CampaignStatus.completed]: false,
    // [CampaignStatus.refunded]: false,
  },
}

// Campaigns-specific hook using the generic persistence hook
export const useCampaignFilters = (brandId: number) => {
  // Use the generic hook with campaign-specific configuration per brand
  const { filters, updateQuery, updateFilter, clearFilters } =
    useFiltersPersistence({
      storageKey: `brand-campaigns-filters-${brandId}`, // Unique localStorage key per brand
      defaultFilters: DEFAULT_CAMPAIGN_FILTERS, // Fallback state
    })

  // Convenience method for updating status filters
  // This handles the nested status object structure automatically
  const updateStatus = useCallback(
    (status: CampaignStatus, value: boolean) => {
      const newStatusFilters = {
        ...filters.status, // Keep other status filters
        [status]: value, // Update only the clicked one
      }
      updateFilter('status', newStatusFilters) // Save to localStorage
    },
    [filters.status, updateFilter],
  )

  return {
    filters, // Current filter state: { query: string (ignored), status: {...} }
    updateStatus, // Function to update status filters (convenience wrapper)
    clearFilters, // Function to reset all filters
    // Note: updateQuery removed since search is not persistent
  }
}
